import isArray from "lodash.isarray";
import isEqual from "lodash.isequal";
import isObject from "lodash.isobject";
import transform from "lodash.transform";

import {
  ExperienceListDto,
  ExperienceListVariationDto,
  ModKeys,
  OfferDto,
  ScheduleDto,
} from "@intelligems/ig-types";
import {
  getEndedAtTs,
  getPausedAtTs,
  getStartedAtTs,
} from "@intelligems/ig-utils";
import { FoundExperimentDetails } from "@src/types/experience";
import { shortDateTimeConverter } from "@src/utils/dateConverters";

export const DISCOUNT_TIER_LIMIT = 5;

export const getPersonalizationDuration = (
  experience: ExperienceListDto,
  storeCurrencyLocale: string,
  schedules?: ScheduleDto[]
) => {
  const pausedAtTs = getPausedAtTs(experience.experienceActions)?.actionTs;
  const endedAtTs = getEndedAtTs(experience.experienceActions)?.actionTs;
  const latestStartedAt = getStartedAtTs(
    experience.experienceActions
  )?.actionTs;

  if (schedules && schedules.length > 0) {
    const experienceSchedules = schedules.filter(
      (schedule) => schedule.experienceId === experience.id
    );

    if (experienceSchedules.length > 0) {
      const startSchedule = experienceSchedules.find(
        (schedule) => schedule.action === "start"
      );
      const pauseSchedule = experienceSchedules.find(
        (schedule) => schedule.action === "pause"
      );

      if (startSchedule) {
        const startDate = shortDateTimeConverter(
          startSchedule.executionTime,
          storeCurrencyLocale
        );

        if (pauseSchedule) {
          const pauseDate = shortDateTimeConverter(
            pauseSchedule.executionTime,
            storeCurrencyLocale
          );
          return `Scheduled: ${startDate} - ${pauseDate}`;
        } else {
          return `Scheduled: ${startDate} onwards`;
        }
      }
    }
  }

  if (experience.status === "pending") {
    return `Created ${shortDateTimeConverter(
      experience.createdAtTs,
      storeCurrencyLocale
    )}`;
  }
  if (experience.status === "started") {
    return `Active since ${shortDateTimeConverter(
      latestStartedAt,
      storeCurrencyLocale
    )}`;
  }
  return `${shortDateTimeConverter(
    latestStartedAt,
    storeCurrencyLocale
  )} - ${shortDateTimeConverter(pausedAtTs ?? endedAtTs, storeCurrencyLocale)}`;
};

export const isUsedInExperiment = (
  experienceList: ExperienceListDto[],
  campaignId: string | null
): FoundExperimentDetails => {
  let experimentFound: FoundExperimentDetails = {
    name: undefined,
    id: undefined,
  };

  for (const experiment of experienceList) {
    for (const group of experiment.variations) {
      if (group.testExperienceId === campaignId && !experiment.endedAtTs) {
        experimentFound = { name: experiment.name, id: experiment.id };
        break;
      }
    }
  }

  return experimentFound;
};

export const isUsedInPersonalization = (
  personalizationsList: ExperienceListDto[],
  experienceVariations: ExperienceListVariationDto[]
): FoundExperimentDetails[] => {
  const matchingPersonalizations: FoundExperimentDetails[] = [];

  for (const personalization of personalizationsList) {
    for (const variation of experienceVariations) {
      if (
        variation.testExperienceId === personalization.id &&
        !personalization.endedAtTs
      ) {
        matchingPersonalizations.push({
          name: personalization.name,
          id: personalization.id,
        });
        break;
      }
    }
  }

  return matchingPersonalizations;
};

export const difference = (origObj: any, newObj: any): any => {
  function changes(newObj: any, origObj: any) {
    let arrayIndexCounter = 0;
    return transform(newObj, function (result, value, key) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;
        // @ts-ignore
        result[resultKey] =
          isObject(value) && isObject(origObj[key])
            ? changes(value, origObj[key])
            : value;
      }
    });
  }

  return changes(newObj, origObj);
};

export const isFreeShippingDisabled = (
  discountState: OfferDto,
  currTierIndex: number
) => {
  const { tiers } = discountState;
  const tier = tiers[currTierIndex];
  if (tier.isFreeShipping) {
    for (let i = 0; i < currTierIndex; i++) {
      if (tiers[i].isFreeShipping) {
        return true;
      }
    }
  } else {
    for (let i = currTierIndex + 1; i < tiers.length; i++) {
      if (!tiers[i].isFreeShipping) {
        return true;
      }
    }
  }

  return false;
};

export const areOtherOffersDisabled = (
  key: ModKeys,
  modificationsInUse: string[],
  tempModsToAdd: string[],
  isHeadless: boolean,
  hasFeatureAccess: boolean,
  isPersonalizationActive: boolean,
  shouldDuplicateProducts: boolean,
  showInactiveSubWarning,
  isViewMode: boolean,
  isPersonalization: boolean,
  isUnderOfferLimit: boolean
) => {
  if (isViewMode) {
    return {
      isDisabled: true,
      reason:
        "Please unarchive this personalization to add more modifications.",
    };
  }
  if (showInactiveSubWarning) {
    return {
      isDisabled: true,
      reason: "Approve billing to add modifications to this personalization.",
    };
  }
  if (isPersonalizationActive && !hasFeatureAccess) {
    return {
      isDisabled: true,
      reason: "Ineligible mod and active personalization",
    };
  }

  if (key === "price" && shouldDuplicateProducts && isPersonalization) {
    return {
      isDisabled: true,
      reason: "Duplicate products are not supported for experiences.",
    };
  }
  if (modificationsInUse.length === 0) {
    return { isDisabled: false, reason: "" };
  }

  if (modificationsInUse.includes(key)) {
    return {
      isDisabled: true,
      reason: "This modification is already in use.",
      showChecked: true,
    };
  }
  if (key === "onsiteEdits" && isHeadless) {
    return {
      isDisabled: true,
      reason:
        "Content edits are unavailable for headless experiences. Dynamically replace content on your site directly.",
    };
  }
  if (
    (key === "tieredDiscount" ||
      key === "cartDiscount" ||
      key === "freeGift" ||
      key === "freeShipping" ||
      key === "offer") &&
    !isUnderOfferLimit
  ) {
    return {
      isDisabled: true,
      reason:
        "You have reached your offer limit. Archive unused offer experiences or tests.",
    };
  }
  const modificationDisabledReasons = {
    freeGift: [
      "tieredDiscount",
      "cartDiscount",
      "freeShipping",
      "price",
      "offer",
    ],
    tieredDiscount: [
      "freeGift",
      "cartDiscount",
      "freeShipping",
      "price",
      "offer",
    ],
    cartDiscount: [
      "freeGift",
      "tieredDiscount",
      "freeShipping",
      "price",
      "offer",
    ],
    freeShipping: [
      "freeGift",
      "tieredDiscount",
      "cartDiscount",
      "price",
      "offer",
    ],
    template: ["theme"],
    theme: ["template"],
    price: [
      "tieredDiscount",
      "cartDiscount",
      "freeGift",
      "freeShipping",
      "offer",
    ],
    offer: [
      "price",
      "tieredDiscount",
      "cartDiscount",
      "freeGift",
      "freeShipping",
    ],
  };

  for (const modification in modificationDisabledReasons) {
    if ([...modificationsInUse, ...tempModsToAdd].includes(modification)) {
      if (key === modification && modificationsInUse.includes(key)) {
        return {
          isDisabled: true,
          reason: "This modification is already in use.",
        };
      }
      if (modificationDisabledReasons[modification].includes(key)) {
        return {
          isDisabled: true,
          reason:
            key === "template"
              ? "A theme modification is already in use and cannot be combined with a template modification."
              : key === "theme"
                ? "A template modification is already in use and cannot be combined with a theme modification."
                : key === "price"
                  ? "A price modification cannot be combined with an offer modification."
                  : key === "offer" && modification === "price"
                    ? "An offer modification cannot be combined with a price modification."
                    : "Another offer modification is already in use.",
        };
      }
    }
  }

  return { isDisabled: false, reason: "" };
};
